import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { money, num } from "../lib/format";
import { useCountry, countryInfo } from "../context/CountryContext";
import { Wallet, WarningCircle, DownloadSimple, Scales, ArrowsLeftRight } from "@phosphor-icons/react";

// Month string for default — current month, format YYYY-MM
const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function RoadExpenses() {
  const { scope, filterParam, isAll, COUNTRIES } = useCountry();
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { month };
      if (filterParam) params.country = filterParam;
      const { data } = await api.get("/road-expenses/summary", { params });
      setData(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, filterParam]);

  const exportCsv = () => {
    const token = localStorage.getItem("access_token") || "";
    const params = new URLSearchParams({ month, token });
    if (filterParam) params.set("country", filterParam);
    // Use axios for authenticated download via blob, but the simplest UX is a click
    api.get("/road-expenses/export", {
      params: { month, ...(filterParam ? { country: filterParam } : {}) },
      responseType: "blob",
    }).then(({ data }) => {
      const blob = new Blob([data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `road-expenses-${month}.csv`; a.click();
      URL.revokeObjectURL(url);
    });
  };

  const currencies = useMemo(() => Object.keys(data?.totals_by_currency || {}), [data]);
  const subtitle = isAll ? "Finance audit · All countries" : `Finance audit · ${COUNTRIES[scope].flag} ${COUNTRIES[scope].name}`;

  return (
    <div data-testid="road-expenses-page">
      <PageHeader
        subtitle={subtitle}
        title="Road Expenses"
        action={
          <div className="flex items-center gap-2">
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              data-testid="road-month-picker"
              className="border border-slate-300 bg-white px-3 py-2.5 text-sm font-mono-data rounded-md"
            />
            <button
              onClick={exportCsv}
              data-testid="road-export-csv"
              className="border border-slate-300 bg-white text-ps-deep px-3 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-slate-50 text-sm"
            >
              <DownloadSimple size={16} weight="bold" /> Export CSV
            </button>
          </div>
        }
      />

      {loading || !data ? (
        <div className="p-8"><div className="indus-bar w-1/3" /></div>
      ) : (
        <>
          {/* Totals strip — split per currency */}
          <div className="px-8 py-4 bg-slate-100 border-b border-slate-200 flex gap-6 flex-wrap items-end" data-testid="road-totals">
            {currencies.length === 0 ? (
              <div className="text-sm text-slate-500 italic">No transfers recorded for {month}.</div>
            ) : (
              currencies.map((cur) => {
                const t = data.totals_by_currency[cur];
                return (
                  <div key={cur} className="border-l border-slate-200 pl-6 first:border-l-0 first:pl-0" data-testid={`road-totals-${cur}`}>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 flex items-center gap-1">
                      <span className="px-1.5 py-0.5 rounded-sm bg-slate-200 text-slate-700 font-bold text-[9px]">{cur}</span>
                    </div>
                    <div className="flex gap-5 mt-1">
                      <div><div className="text-[10px] text-slate-500">Sent</div><div className="font-mono-data tabular text-lg">{money(t.sent, cur)}</div></div>
                      <div><div className="text-[10px] text-slate-500">Settled</div><div className="font-mono-data tabular text-lg text-lime-700">{money(t.settled, cur)}</div></div>
                      <div><div className="text-[10px] text-slate-500">Outstanding</div><div className="font-mono-data tabular text-lg text-amber-700">{money(t.outstanding, cur)}</div></div>
                      <div><div className="text-[10px] text-slate-500">Transfers</div><div className="font-mono-data tabular text-lg text-ps-deep">{num(t.transfers)}</div></div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Discrepancy banner */}
          {data.discrepancies && data.discrepancies.length > 0 && (
            <div className="bg-amber-50 border-y border-amber-200 px-8 py-3" data-testid="road-discrepancies">
              <div className="flex items-start gap-2 text-amber-900">
                <WarningCircle size={20} weight="bold" className="text-amber-700 mt-0.5" />
                <div>
                  <div className="font-semibold text-sm">{data.discrepancies.length} audit flag{data.discrepancies.length === 1 ? "" : "s"} for {month}</div>
                  <ul className="text-xs space-y-0.5 mt-1">
                    {data.discrepancies.slice(0, 6).map((d, i) => (
                      <li key={i} data-testid={`discrepancy-${i}`}>
                        <span className="font-mono-data font-semibold">{d.driver_name}</span>
                        <span className="ml-2 text-amber-700">— {d.message}</span>
                      </li>
                    ))}
                    {data.discrepancies.length > 6 && (
                      <li className="text-amber-700">+ {data.discrepancies.length - 6} more</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* By driver */}
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="road-by-driver">
              <div className="px-6 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wallet size={16} weight="duotone" className="text-ps-blue" />
                  <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">By driver</div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{data.by_driver.length} rows</div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left">Driver</th>
                    <th className="px-3 py-2 text-left w-16">Ccy</th>
                    <th className="px-3 py-2 text-right">Sent</th>
                    <th className="px-3 py-2 text-right">Settled</th>
                    <th className="px-3 py-2 text-right">Outstanding</th>
                    <th className="px-3 py-2 text-center" title="Transfers">Tx</th>
                    <th className="px-3 py-2 text-center" title="Activities ran this month">Act.</th>
                    <th className="px-3 py-2 text-right">Fuel</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.by_driver.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-500">No transfers nor activities for {month}.</td></tr>}
                  {data.by_driver.map((r, i) => {
                    const flag = (r.sent > 0 && r.activities === 0) || (r.sent === 0 && r.activities > 0);
                    return (
                      <tr key={`${r.driver_name}-${r.currency}-${i}`} className={`hover:bg-slate-50 ${flag ? "bg-amber-50/40" : ""}`} data-testid={`road-driver-${r.driver_name}`}>
                        <td className="px-3 py-2 font-semibold text-ps-deep">{r.driver_name}</td>
                        <td className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-600">{r.currency}</td>
                        <td className="px-3 py-2 text-right font-mono-data tabular">{r.sent ? money(r.sent, r.currency === "—" ? "GHS" : r.currency) : "—"}</td>
                        <td className="px-3 py-2 text-right font-mono-data tabular text-lime-700">{r.settled ? money(r.settled, r.currency === "—" ? "GHS" : r.currency) : "—"}</td>
                        <td className="px-3 py-2 text-right font-mono-data tabular text-amber-700">{r.outstanding ? money(r.outstanding, r.currency === "—" ? "GHS" : r.currency) : "—"}</td>
                        <td className="px-3 py-2 text-center font-mono-data text-slate-600">{r.transfers || "—"}</td>
                        <td className="px-3 py-2 text-center font-mono-data text-ps-blue font-semibold">{r.activities || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono-data tabular text-slate-600 text-xs">{r.fuel_l ? `${num(r.fuel_l)} L` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* By truck */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="road-by-truck">
              <div className="px-6 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Scales size={16} weight="duotone" className="text-ps-blue" />
                  <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">By truck</div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{data.by_truck.length} rows</div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2 text-left">Truck</th>
                    <th className="px-3 py-2 text-left w-16">Ccy</th>
                    <th className="px-3 py-2 text-right">Sent</th>
                    <th className="px-3 py-2 text-center" title="Transfers">Tx</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.by_truck.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-500 text-sm italic">No truck-tagged transfers.</td></tr>}
                  {data.by_truck.map((r, i) => (
                    <tr key={`${r.vehicle_number}-${r.currency}-${i}`} className="hover:bg-slate-50" data-testid={`road-truck-${r.vehicle_number}`}>
                      <td className="px-3 py-2 font-mono-data text-ps-blue font-semibold">{r.vehicle_number}</td>
                      <td className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-600">{r.currency}</td>
                      <td className="px-3 py-2 text-right font-mono-data tabular">{money(r.sent, r.currency)}</td>
                      <td className="px-3 py-2 text-center font-mono-data text-slate-600">{r.transfers}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="px-8 pb-8 text-xs text-slate-500 flex items-center gap-2">
            <ArrowsLeftRight size={12} />
            Finance audit lens — same data as <span className="font-semibold text-slate-700">Money on Road</span>, cross-referenced against driver activities. Use the month picker to compare past periods.
          </div>
        </>
      )}
    </div>
  );
}
